import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Button, SelectField, TextField, type ListColumn } from "@wilco/ui";
import { useDeferredValue, useState } from "react";
import { DocumentList } from "../docs/DocumentList.tsx";
import { FormPage, useDocumentForm } from "../docs/DocumentForm.tsx";
import { serverPage } from "../docs/serverPage.ts";
import { t, type MessageKey } from "../i18n.ts";
import { catalogApi, variantName, type Product } from "./api.ts";
import "../production/cards.css";
import { directoryApi, type FirmwareRow, type SkillRow, type TemplateRow, type VersionRow } from "./directoryApi.ts";

/*
 * «Операції» раздела «Довідник» (handoff 3 ноября): товары и значения
 * «Версії» списком, справочник версий прошивки и квалификации сотрудников.
 * Прошивки и квалификации ведёт мастер дільниці; хранятся они в маршруте,
 * поэтому пути /api/v1/route. Списки страницами сервера (CursorPage).
 */

const DIRECTORY = ["catalog", "directory"] as const;

/** Чипы «Активні / Архів»: сервер фильтрует по признаку архива. */
const archiveChips = () => [
  { value: "active", label: t("directory.active") },
  { value: "archived", label: t("catalog.archived") },
];
const archived = (status: string | null): boolean | null => (status === "archived" ? true : status === "active" ? false : null);

const ArchivedBadge = ({ on }: { on: boolean }) => (on ? <Badge>{t("catalog.archived")}</Badge> : <Badge tone="success">{t("directory.active")}</Badge>);

const KIND: Record<TemplateRow["kind"], MessageKey> = {
  COMPONENT: "catalog.kind.COMPONENT",
  PRODUCT: "catalog.kind.PRODUCT",
  SERVICE: "catalog.kind.SERVICE",
};

/** Известные квалификации маршрута; мастер может дать и свою, по имени шага карты. */
export const SKILLS: Record<string, MessageKey> = {
  firmware: "directory.skill.firmware",
  "firmware-repair": "directory.skill.firmwareRepair",
};
export const skillLabel = (skill: string): string => (SKILLS[skill] ? t(SKILLS[skill]) : skill);

const templatesRoute = getRouteApi("/app/catalog/templates");

/** Товары списком: поиск по названию и артикулу варианта, архив отдельным чипом. */
export function TemplatesPage() {
  const search = templatesRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<TemplateRow>[] = [
    { key: "name", header: t("catalog.name"), kind: "title", render: (r) => r.name },
    { key: "codes", header: t("catalog.code"), kind: "text", width: "12rem", render: (r) => <span className="w-num">{r.codes.join(", ") || "—"}</span> },
    { key: "category", header: t("catalog.category"), kind: "text", render: (r) => r.categoryPath },
    { key: "kind", header: t("catalog.kind"), kind: "text", width: "8rem", render: (r) => t(KIND[r.kind]) },
    { key: "versions", header: t("directory.versions"), kind: "text", width: "8rem", render: (r) => r.versions.join(", ") },
    { key: "state", header: t("docs.status"), kind: "text", width: "7rem", render: (r) => <ArchivedBadge on={r.archived} /> },
  ];
  return (
    <DocumentList
      title={t("directory.templates")}
      icon="stock"
      queryKey={[...DIRECTORY, "templates"]}
      load={async (q) => serverPage(await directoryApi.templates({ ...q, archived: archived(q.status) }))}
      columns={columns}
      rowKey={(r) => r.id}
      onOpen={(r) => void navigate({ to: "/catalog/templates/$id", params: { id: r.id } })}
      statuses={archiveChips()}
      sorts={[{ value: "created", label: t("docs.sort.created") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/catalog/templates", search: next, replace: true })}
      searchHint={t("directory.templatesHint")}
      create={{ label: t("catalog.new"), to: "/catalog/products/new" }}
      empty={{ title: t("directory.templatesEmpty") }}
    />
  );
}

const versionsRoute = getRouteApi("/app/catalog/versions");

/** Значения «Версії» всех изделий с вариантом и артикулом (CAT-11). */
export function VersionsPage() {
  const search = versionsRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<VersionRow>[] = [
    { key: "version", header: t("directory.version"), kind: "title", width: "8rem", render: (r) => <span className="w-num">{r.version}</span> },
    { key: "template", header: t("catalog.name"), kind: "text", render: (r) => r.templateName },
    { key: "code", header: t("catalog.code"), kind: "text", width: "10rem", render: (r) => <span className="w-num">{r.code ?? "—"}</span> },
    { key: "state", header: t("docs.status"), kind: "text", width: "7rem", render: (r) => <ArchivedBadge on={r.archived} /> },
  ];
  return (
    <DocumentList
      title={t("directory.versionsTitle")}
      icon="stock"
      queryKey={[...DIRECTORY, "versions"]}
      load={async (q) => serverPage(await directoryApi.versions({ ...q, archived: archived(q.status) }))}
      columns={columns}
      rowKey={(r) => `${r.templateId}/${r.version}`}
      onOpen={(r) => void navigate({ to: "/catalog/templates/$id", params: { id: r.templateId } })}
      statuses={archiveChips()}
      sorts={[{ value: "created", label: t("docs.sort.created") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/catalog/versions", search: next, replace: true })}
      searchHint={t("directory.versionsHint")}
      create={{ label: t("catalog.newVersion"), to: "/catalog/versions/new" }}
      empty={{ title: t("directory.versionsEmpty") }}
    />
  );
}

/**
 * Выбор товара справочника поиском (CAT-09: из формы товар не создаётся).
 * Выбранный показан строкой с кнопкой «Змінити».
 */
export function CatalogPick({ label, value, onChange, error, products = false }: {
  label: string;
  value: Product | null;
  onChange: (p: Product | null) => void;
  error?: string;
  /** Только изделия (вид PRODUCT): у карт и прошивок другого не бывает. */
  products?: boolean;
}) {
  const [text, setText] = useState("");
  const q = useDeferredValue(text.trim());
  const found = useQuery({ queryKey: ["catalog", "pick", q], queryFn: () => catalogApi.search(q), enabled: q.length >= 2 && !value });
  if (value) {
    return (
      <div className="doc-pick">
        <span className="doc-pick-label">{label}</span>
        <span>
          <span className="w-num">{value.code ?? "—"}</span> {variantName(value)}
        </span>
        <Button variant="ghost" onClick={() => onChange(null)}>
          {t("directory.change")}
        </Button>
      </div>
    );
  }
  const items = (found.data ?? []).map((m) => m.product).filter((p) => !products || p.kind === "PRODUCT");
  return (
    <div className="product-search">
      <TextField label={label} placeholder={t("catalog.searchHint")} value={text} onChange={(e) => setText(e.target.value)} error={error} autoComplete="off" />
      {q.length >= 2 && found.data ? (
        <ul className="product-search-list">
          {items.length === 0 ? <li className="w-muted">{t("palette.empty")}</li> : null}
          {items.map((p) => (
            <li key={p.id}>
              <button type="button" className="product-search-item" onClick={() => onChange(p)}>
                <span className="w-num">{p.code ?? "—"}</span> {variantName(p)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface NewVersion {
  product: Product | null;
  name: string;
  code: string;
}

/** Новое значение «Версії»: ещё один вариант изделия со своим артикулом (CAT-11). */
export function NewVersionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useDocumentForm<NewVersion, unknown>({
    initial: { product: null, name: "", code: "" },
    validate: (v) => ({ product: v.product ? undefined : t("docs.required"), name: v.name.trim() ? undefined : t("docs.required") }),
    submit: (v) => catalogApi.addVersion(v.product?.templateId ?? "", v.name.trim(), v.code.trim() || null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog"] });
      void navigate({ to: "/catalog/versions", search: {}, replace: true });
    },
    fieldOf: (code) => (code === "catalog.code_taken" ? "code" : code.startsWith("catalog.version") ? "name" : undefined),
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("catalog.newVersion")} back="/catalog/versions" submitLabel={t("catalog.addVersion")} form={form}>
      <CatalogPick label={t("directory.product")} value={values.product} onChange={(p) => set("product", p)} error={errors.product} products />
      <TextField label={t("directory.version")} value={values.name} onChange={(e) => set("name", e.target.value)} error={errors.name} />
      <TextField label={t("catalog.code")} hint={t("catalog.codeHint")} value={values.code} onChange={(e) => set("code", e.target.value)} error={errors.code} />
    </FormPage>
  );
}

const firmwareRoute = getRouteApi("/app/catalog/firmware");

/** Справочник версий прошивки изделий (RTE-19): архивная не выбирается на «Прошивці». */
export function FirmwarePage() {
  const search = firmwareRoute.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const archive = useMutation({
    mutationFn: (r: FirmwareRow) => directoryApi.archiveFirmware(r.productId, r.version),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [...DIRECTORY, "firmware"] }),
  });
  const columns: ListColumn<FirmwareRow>[] = [
    { key: "version", header: t("directory.firmwareVersion"), kind: "title", width: "9rem", render: (r) => <span className="w-num">{r.version}</span> },
    { key: "product", header: t("directory.product"), kind: "text", render: (r) => r.productName },
    { key: "state", header: t("docs.status"), kind: "text", width: "7rem", render: (r) => <ArchivedBadge on={!r.active} /> },
    {
      key: "action",
      header: "",
      kind: "text",
      width: "9rem",
      render: (r) =>
        r.active ? (
          <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate(r)}>
            {t("directory.toArchive")}
          </Button>
        ) : null,
    },
  ];
  return (
    <DocumentList
      title={t("directory.firmware")}
      icon="route"
      queryKey={[...DIRECTORY, "firmware"]}
      load={async (q) => serverPage(await directoryApi.firmware({ ...q, active: archived(q.status) === null ? null : !archived(q.status) }))}
      columns={columns}
      rowKey={(r) => `${r.productId}/${r.version}`}
      statuses={archiveChips()}
      sorts={[{ value: "created", label: t("docs.sort.created") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/catalog/firmware", search: next, replace: true })}
      searchHint={t("directory.firmwareHint")}
      create={{ label: t("directory.newFirmware"), to: "/catalog/firmware/new" }}
      empty={{ title: t("directory.firmwareEmpty") }}
    />
  );
}

interface NewFirmware {
  product: Product | null;
  version: string;
}

export function NewFirmwarePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useDocumentForm<NewFirmware, unknown>({
    initial: { product: null, version: "" },
    validate: (v) => ({ product: v.product ? undefined : t("docs.required"), version: v.version.trim() ? undefined : t("docs.required") }),
    submit: (v, key) => directoryApi.addFirmware(v.product?.id ?? "", v.version.trim(), key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...DIRECTORY, "firmware"] });
      void navigate({ to: "/catalog/firmware", search: {}, replace: true });
    },
    fieldOf: (code) => (code.startsWith("firmware.") ? "version" : undefined),
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("directory.newFirmware")} back="/catalog/firmware" submitLabel={t("catalog.create")} form={form}>
      <CatalogPick label={t("directory.product")} value={values.product} onChange={(p) => set("product", p)} error={errors.product} products />
      <TextField label={t("directory.firmwareVersion")} value={values.version} onChange={(e) => set("version", e.target.value)} error={errors.version} />
    </FormPage>
  );
}

const skillsRoute = getRouteApi("/app/catalog/skills");

/** Квалификации сотрудников (RTE-04): даёт и снимает мастер дільниці. */
export function SkillsPage() {
  const search = skillsRoute.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: (r: SkillRow) => directoryApi.revokeSkill(r.employeeId, r.skill),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [...DIRECTORY, "skills"] }),
  });
  const columns: ListColumn<SkillRow>[] = [
    { key: "employee", header: t("directory.employee"), kind: "title", render: (r) => r.employeeName },
    { key: "skill", header: t("directory.skill"), kind: "text", render: (r) => skillLabel(r.skill) },
    {
      key: "action",
      header: "",
      kind: "text",
      width: "9rem",
      render: (r) => (
        <Button variant="ghost" disabled={revoke.isPending} onClick={() => revoke.mutate(r)}>
          {t("directory.revoke")}
        </Button>
      ),
    },
  ];
  return (
    <DocumentList
      title={t("directory.skills")}
      icon="hr"
      queryKey={[...DIRECTORY, "skills"]}
      load={async (q) => serverPage(await directoryApi.skills(q))}
      columns={columns}
      rowKey={(r) => `${r.employeeId}/${r.skill}`}
      sorts={[{ value: "created", label: t("docs.sort.created") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/catalog/skills", search: next, replace: true })}
      searchHint={t("directory.skillsHint")}
      create={{ label: t("directory.newSkill"), to: "/catalog/skills/new" }}
      empty={{ title: t("directory.skillsEmpty") }}
    />
  );
}

interface NewSkill {
  employeeId: string;
  skill: string;
}

export function NewSkillPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const employees = useQuery({ queryKey: ["hr", "employees"], queryFn: directoryApi.employees });
  const form = useDocumentForm<NewSkill, unknown>({
    initial: { employeeId: "", skill: "firmware" },
    validate: (v) => ({ employeeId: v.employeeId ? undefined : t("docs.required"), skill: v.skill.trim() ? undefined : t("docs.required") }),
    submit: (v, key) => directoryApi.grantSkill(v.employeeId, v.skill.trim(), key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...DIRECTORY, "skills"] });
      void navigate({ to: "/catalog/skills", search: {}, replace: true });
    },
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("directory.newSkill")} back="/catalog/skills" submitLabel={t("catalog.create")} form={form}>
      <SelectField
        label={t("directory.employee")}
        value={values.employeeId}
        onChange={(e) => set("employeeId", e.target.value)}
        options={[{ value: "", label: t("docs.choose") }, ...(employees.data ?? []).filter((e) => !e.archived).map((e) => ({ value: e.id, label: e.name }))]}
        error={errors.employeeId}
      />
      <TextField label={t("directory.skill")} hint={t("directory.skillHint")} list="route-skills" value={values.skill} onChange={(e) => set("skill", e.target.value)} error={errors.skill} />
      <datalist id="route-skills">
        {Object.keys(SKILLS).map((s) => (
          <option key={s} value={s}>
            {skillLabel(s)}
          </option>
        ))}
      </datalist>
    </FormPage>
  );
}
